// Small fetch helpers for the No Fuss Tutors API (nftserver).
export const NFT_BASE_URL = process.env.REACT_APP_BASE_URL || '';
const AUTH_KEY = process.env.REACT_APP_PERSONAL_AUTH_KEY || '';

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export type QueryValue = string | number | boolean | undefined | null;

export const toQuery = (params: Record<string, QueryValue>) => {
  const search = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  });
  const text = search.toString();
  return text ? `?${text}` : '';
};

/** Appends the dashboard key that nftserver's OriginControl accepts. */
export const withKey = (url: string) => `${url}${url.includes('?') ? '&' : '?'}key=${AUTH_KEY}`;

export const nftUrl = (path: string, params: Record<string, QueryValue> = {}) =>
  withKey(`${NFT_BASE_URL}${path}${toQuery(params)}`);

export async function requestJSON<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...(init.headers || {}) },
  });
  let body: any = null;
  try {
    body = await response.json();
  } catch (err) {
    body = null;
  }
  if (!response.ok) {
    throw new ApiError((body && (body.message || body.error)) || `Request failed (${response.status})`, response.status);
  }
  return body as T;
}

export const isAbort = (err: any) => err && err.name === 'AbortError';

// Signed pass from POST /api/login/admin. nftserver routes that expose customer
// data (e.g. /api/readers-admin) require it as a Bearer token.
const DASHBOARD_TOKEN_KEY = 'nft_dashboard_token';

export const getDashboardToken = () => {
  try {
    return window.localStorage.getItem(DASHBOARD_TOKEN_KEY);
  } catch (err) {
    return null;
  }
};

export const setDashboardToken = (token: string | null) => {
  try {
    if (token) window.localStorage.setItem(DASHBOARD_TOKEN_KEY, token);
    else window.localStorage.removeItem(DASHBOARD_TOKEN_KEY);
  } catch (err) {
    // storage blocked: the user signs in again next visit
  }
};

export const dashboardJSON = <T>(path: string, params: Record<string, QueryValue> = {}, init: RequestInit = {}) => {
  const token = getDashboardToken();
  return requestJSON<T>(nftUrl(path, params), {
    ...init,
    headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(init.headers || {}) },
  });
};
